import { parseUtcMs } from '../models/flock-telemetry.utils';
import { HistoryPoint, HostMetric } from './host-metric.models';
import { HostReading } from './attention-inputs.logic';
import { HOST_SAMPLE_WINDOW_MS, HostSample } from './machines.logic';

/** Host history lookup (HostMetricsService.getHistory), injectable for tests. */
export type HostHistory = (hostId: string) => HistoryPoint[];

/**
 * Newest hostMetrics per host (cpu/mem/disk) plus the last minute's samples,
 * as the attention model's host readings (WI 34293).
 */
export function buildHostReadings(
  hosts: readonly HostMetric[],
  getHistory: HostHistory
): HostReading[] {
  const out: HostReading[] = [];
  for (const h of hosts) {
    const newest = parseUtcMs(h.lastHeartbeat);
    if (newest === null) continue;
    const free =
      h.disk?.totalBytes != null && h.disk?.usedBytes != null
        ? h.disk.totalBytes - h.disk.usedBytes
        : null;
    out.push({
      host: h.hostname || h.hostId,
      atMs: newest,
      cpuPct: h.cpu?.percent ?? null,
      memPct: h.mem?.percent ?? null,
      diskPct: h.disk?.percent ?? null,
      diskFreeBytes: free,
    });
    for (const p of getHistory(h.hostId)) {
      const at = parseUtcMs(p.ts);
      if (at === null || at === newest || newest - at > HOST_SAMPLE_WINDOW_MS)
        continue;
      out.push({
        host: h.hostname || h.hostId,
        atMs: at,
        cpuPct: p.cpu,
        memPct: p.mem,
        diskPct: p.disk,
        diskFreeBytes: null,
      });
    }
  }
  return out;
}

/** hostMetrics samples from the last minute per host, newest first. */
export function buildHostSamples(
  hosts: readonly HostMetric[],
  getHistory: HostHistory
): HostSample[] {
  const out: HostSample[] = [];
  for (const h of hosts) {
    const newest = parseUtcMs(h.lastHeartbeat);
    if (newest === null) continue;
    for (const p of getHistory(h.hostId)) {
      const at = parseUtcMs(p.ts);
      if (at === null || newest - at > HOST_SAMPLE_WINDOW_MS) continue;
      out.push({
        hostId: h.hostId,
        hostname: h.hostname,
        atMs: at,
        memPercent: p.mem,
        memTotalBytes: h.mem.totalBytes,
        diskPercent: p.disk,
        diskTotalBytes: h.disk.totalBytes,
      });
    }
  }
  return out;
}
